import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import type { CommunicationType, EmailAttachmentMetadata, Pageable, WrapperPageableResponse, WrapperResponse } from '../core/models';

export type CommunicationStatus = 'PENDING' | 'SENT' | 'FAILED';
export type CommunicationChannel = 'EMAIL' | 'INTERNAL';

export interface CommunicationDTO {
  id?: number;
  templateId?: number;
  templateName?: string;
  templateType?: CommunicationType;
  sentByUserId?: number;
  sentByUserName?: string;
  sentByUserEmail?: string;
  saleId?: number;
  subscriptionId?: number;
  chargeId?: number;
  saleLinkId?: number;
  channel?: CommunicationChannel;
  toEmail?: string;
  ccEmails?: string[];
  subject?: string;
  messageBody?: string;
  status?: CommunicationStatus;
  errorMessage?: string;
  sentAt?: string;
  openedAt?: string;
  emailAuditId?: number;
  state?: string;
  createdAt?: string;
  updatedAt?: string;
  attachments?: EmailAttachmentMetadata[];
}

export interface ListCommunicationFilterSpec {
  saleId?: number;
  subscriptionId?: number;
  chargeId?: number;
  saleLinkId?: number;
  status?: CommunicationStatus;
  channel?: CommunicationChannel;
  templateId?: number;
  sentByUserId?: number;
}

export interface SendCommunicationAttachmentRequest {
  name: string;
  contentType?: string;
  contentBase64: string;
}

export interface SendCommunicationRequest {
  templateId: number;
  saleId?: number;
  subscriptionId?: number;
  chargeId?: number;
  toEmail: string;
  subject?: string;
  messageBody?: string;
  ccEmails?: string[];
  variables?: Record<string, string>;
  attachmentFileIds?: number[];
  attachments?: SendCommunicationAttachmentRequest[];
}

export interface CreateInternalCommunicationRequest {
  saleId?: number;
  subscriptionId?: number;
  chargeId?: number;
  subject?: string;
  messageBody: string;
}

export type WrapperPageableResponseCommunicationDTO = WrapperPageableResponse<CommunicationDTO>;
export type WrapperResponseCommunicationDTO = WrapperResponse<CommunicationDTO>;

@Injectable({ providedIn: 'root' })
export class CommunicationService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = `${environment.apiUrl}/communications`;

  list(
    spec: ListCommunicationFilterSpec = {},
    pageable: Pageable = { page: 0, size: 20, sort: ['createdAt,desc'] }
  ): Observable<WrapperPageableResponseCommunicationDTO> {
    let params = new HttpParams();
    Object.entries(spec).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params = params.append(key, String(value));
      }
    });
    params = params.append('page', String(pageable.page));
    params = params.append('size', String(pageable.size));
    pageable.sort?.forEach((sort) => {
      params = params.append('sort', sort);
    });
    return this.http.get<WrapperPageableResponseCommunicationDTO>(`${this.apiUrl}/list`, { params });
  }

  getById(id: number): Observable<WrapperResponseCommunicationDTO> {
    return this.http.get<WrapperResponseCommunicationDTO>(`${this.apiUrl}/${id}`);
  }

  send(request: SendCommunicationRequest): Observable<WrapperResponseCommunicationDTO> {
    return this.http.post<WrapperResponseCommunicationDTO>(`${this.apiUrl}/send`, request);
  }

  createInternal(request: CreateInternalCommunicationRequest): Observable<WrapperResponseCommunicationDTO> {
    return this.http.post<WrapperResponseCommunicationDTO>(`${this.apiUrl}/internal`, request);
  }
}
